import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerBusinessDetailsForm } from 'src/components/customer/customer-business-details-form/CustomerBusinessDetailsForm'
import { CustomerInsuranceForm } from 'src/components/customer/customer-insurance-form/CustomerInsuranceForm'
import { CustomerSignContract } from 'src/components/customer/customer-sign-contract/CustomerSignContract'
import { CustomerOnboardingWelcome } from 'src/components/customer/CustomerOnboardingWelcome'
import { FetchCustomerProfile } from 'src/constants/actionTypes'
import { CustomerUploadBankInformation } from 'src/constants/actionTypes'
import {
  getIsCustomerOnboardingCustomerInformationCompleted,
  getIsCustomerOnboardingStarted,
  getNotCompletedCustomerOnboardingCollectDataTypes,
  getCustomerProfile,
  getCustomerOnboardingStepsCount,
  getIsCustomerOnboardingPaymentMethodCompleted,
} from 'src/selectors/customer'
import { CustomerCollectedDataType } from 'src/constants/customer/enums'
import { FullScreenLoader } from 'src/components/shared/FullScreenLoader'
import { CustomerOnboardingCompleted } from 'src/components/customer/CustomerOnboardingCompleted'
import { CustomerTSAForm } from 'src/components/customer/customer-tsa-form/CustomerTSAForm'
import { CustomerBankPaymentForm } from 'src/components/customer/customer-payment-method-form/BankPaymentForm'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { BankInformationRequest } from 'src/components/customer/customer-payment-method-form/BankPaymentForm.interface'
import { CustomerBusinessInformationForm } from 'src/components/customer/customer-business-information-form/CustomerBusinessInformationForm'

const CustomerOnboardingPageShell = ({ children }: { children: React.ReactNode }) => (
  <div data-testid="customer-account-onboarding-page">{children}</div>
)

const CustomerOnboardingPageComponent = () => {
  const dispatch = useDispatch()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const customerProfile = useSelector(getCustomerProfile)
  const customerOnboardingStepsCount = useSelector(getCustomerOnboardingStepsCount)
  const isCustomerOnboardingStarted = useSelector(getIsCustomerOnboardingStarted)
  const isCustomerOnboardingPartnerInformationCompleted = useSelector(
    getIsCustomerOnboardingCustomerInformationCompleted,
  )
  const isCustomerOnboardingPaymentMethodCompleted = useSelector(
    getIsCustomerOnboardingPaymentMethodCompleted,
  )
  const notCompletedCustomerOnboardingCollectDataTypes = useSelector(
    getNotCompletedCustomerOnboardingCollectDataTypes,
  )

  const [isStartPressed, setIsStartPressed] = useState(false)

  const handleStart = useCallback(() => {
    setIsStartPressed(true)
  }, [])

  useEffect(() => {
    const fetchState = async () => {
      dispatch(FetchCustomerProfile.request())
    }

    fetchState()
  }, [])

  const handleSubmitBankInformation = useCallback(
    async (requestBody: BankInformationRequest) => {
      await asyncDispatchWithErrorToast(CustomerUploadBankInformation.request(requestBody))
    },
    [asyncDispatchWithErrorToast],
  )

  if (!customerProfile) {
    return <FullScreenLoader />
  }

  if (!isCustomerOnboardingStarted && !isStartPressed) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerOnboardingWelcome onNext={handleStart} />
      </CustomerOnboardingPageShell>
    )
  }

  if (!isCustomerOnboardingPartnerInformationCompleted) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerBusinessInformationForm stepNumber={1} stepCount={customerOnboardingStepsCount} />
      </CustomerOnboardingPageShell>
    )
  }

  if (notCompletedCustomerOnboardingCollectDataTypes.includes(CustomerCollectedDataType.TSA)) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerTSAForm stepNumber={2} stepCount={customerOnboardingStepsCount} />
      </CustomerOnboardingPageShell>
    )
  }

  if (
    notCompletedCustomerOnboardingCollectDataTypes.includes(CustomerCollectedDataType.Insurance)
  ) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerInsuranceForm stepNumber={3} stepCount={customerOnboardingStepsCount} />
      </CustomerOnboardingPageShell>
    )
  }

  if (!isCustomerOnboardingPaymentMethodCompleted) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerBankPaymentForm
          stepNumber={5}
          stepCount={customerOnboardingStepsCount}
          showCurrentPaymentMethod={false}
          onSubmitBankInformation={handleSubmitBankInformation}
        />
      </CustomerOnboardingPageShell>
    )
  }

  if (
    notCompletedCustomerOnboardingCollectDataTypes.includes(
      CustomerCollectedDataType.BusinessDetails,
    )
  ) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerBusinessDetailsForm stepNumber={6} stepCount={customerOnboardingStepsCount} />
      </CustomerOnboardingPageShell>
    )
  }

  if (
    notCompletedCustomerOnboardingCollectDataTypes.includes(
      CustomerCollectedDataType.GigsafeContract,
    )
  ) {
    return (
      <CustomerOnboardingPageShell>
        <CustomerSignContract
          stepNumber={customerOnboardingStepsCount}
          stepCount={customerOnboardingStepsCount}
        />
      </CustomerOnboardingPageShell>
    )
  }

  return (
    <CustomerOnboardingPageShell>
      <CustomerOnboardingCompleted />
    </CustomerOnboardingPageShell>
  )
}

export const CustomerOnboardingPage = memo(CustomerOnboardingPageComponent)
